"use client";

import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { ArrowRight } from "lucide-react";
import posthog from "posthog-js";
import { resolveBuyBridgeProductSlug } from "@/config/peptide-links";
import type { BuyBridgeProduct } from "@/lib/calculator/buy-bridge";
import { resolveBuyBridgeMatch } from "@/lib/calculator/direct-add-experiment";
import { InternalSourceLink } from "@/ui/components/analytics/internal-source-link";
import { useBridgeCtaTracking } from "./use-bridge-cta-tracking";

interface ResultProductLinkProps {
	product: BuyBridgeProduct;
	peptideAmount: number;
	locale: string;
	channel: string;
}

/** A quiet, optional product handoff after the complete calculation result. */
export function ResultProductLink(props: ResultProductLinkProps) {
	const searchParams = useSearchParams();
	if (
		searchParams.get("bb") === "0" ||
		!props.product ||
		resolveBuyBridgeProductSlug(props.product.calcSlug) !== props.product.productSlug
	) {
		return null;
	}
	return <LiveResultProductLink {...props} product={props.product} />;
}

function LiveResultProductLink({
	product,
	peptideAmount,
	locale,
	channel,
}: ResultProductLinkProps & { product: NonNullable<BuyBridgeProduct> }) {
	const t = useTranslations("peptideCalculator");
	const matched = resolveBuyBridgeMatch(product, peptideAmount);
	const matchType = !matched
		? "none"
		: matched.doseMg === peptideAmount
			? "exact"
			: (matched.doseMg ?? 0) > peptideAmount
				? "larger_vial"
				: "smaller_vial";
	const { setTrackedElement, trackClick } = useBridgeCtaTracking({
		bridge_surface: "result_link",
		locale,
		channel,
		peptide_slug: product.calcSlug,
		product_slug: product.productSlug,
		variant_id: matched?.id ?? null,
		match_type: matchType,
		cta_action: "view_product",
	});
	const href = `/${locale}/${channel}/products/${product.productSlug}${matched ? `?variant=${encodeURIComponent(matched.id)}` : ""}`;

	return (
		<InternalSourceLink
			ref={setTrackedElement}
			href={href}
			source="calculator"
			detail={product.calcSlug}
			data-testid="calculator-result-product-link"
			className="bg-muted/30 hover:border-primary/60 mt-4 flex min-h-14 items-center justify-between gap-3 rounded-xl border border-border px-3 py-3 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
			onClick={() => {
				trackClick();
				posthog.capture("calculator_cta_clicked", {
					bridge_version: "calculator_bridge_v2",
					cta_source: "result_link",
					cta_action: "view_product",
					peptide_slug: product.calcSlug,
					product_slug: product.productSlug,
					variant_id: matched?.id ?? null,
					match_type: matchType,
					locale,
					channel,
				});
			}}
		>
			<span className="min-w-0">
				<span className="block font-semibold text-foreground">
					{product.name}
					{matched?.doseMg != null ? ` · ${matched.doseMg} mg` : ""}
				</span>
				<span className="mt-0.5 block text-muted-foreground">{t("spoke.viewProduct")}</span>
			</span>
			<span className="flex shrink-0 items-center gap-2 font-semibold tabular-nums text-primary">
				{matched?.priceLabel ?? product.fromPriceLabel}
				<ArrowRight className="h-4 w-4" aria-hidden />
			</span>
		</InternalSourceLink>
	);
}
